// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useEffect, useState, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import { alpha, useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import CellTowerIcon from '@mui/icons-material/CellTower';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SimCardIcon from '@mui/icons-material/SimCard';
import RouterIcon from '@mui/icons-material/Router';
import PublicIcon from '@mui/icons-material/Public';
import TerminalIcon from '@mui/icons-material/Terminal';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { remotePost } from '../utils/remoteApi';
import {
  createCancelableRemoteGet,
  createCancelableRemotePost,
} from '../utils/remoteApiCancelable';
import axios from 'axios';
import { parseOperator } from '../utils/atParser';
import { getGlobalBackoff } from '../utils/exponentialBackoff';
import { useTheme as useAppTheme } from '../context/ThemeContext';

const signalColor = (metric, type) => {
  if (metric == null || typeof metric === 'string') return null;
  const thresholds = {
    RSSI: { good: -70, fair: -85 },
    RSRP: { good: -90, fair: -105 },
    RSRQ: { good: -10, fair: -15 },
    SINR: { good: 10, fair: 0 },
  };
  const t = thresholds[type];
  if (!t) return null;
  if (metric >= t.good) return '#4ade80';
  if (metric >= t.fair) return '#f59e0b';
  return '#ef4444';
};

const signalLabel = (metric, type) => {
  const c = signalColor(metric, type);
  if (!c) return 'N/A';
  if (c === '#4ade80') return 'Good';
  if (c === '#f59e0b') return 'Fair';
  return 'Poor';
};

const signalBarCount = (rssi) => {
  if (rssi == null || typeof rssi === 'string') return 0;
  if (rssi >= -65) return 5;
  if (rssi >= -75) return 4;
  if (rssi >= -85) return 3;
  if (rssi >= -95) return 2;
  if (rssi >= -105) return 1;
  return 0;
};

function SignalBars({ rssi }) {
  const bars = signalBarCount(rssi);
  const theme = useTheme();
  const active =
    bars > 0
      ? bars >= 4
        ? '#4ade80'
        : bars >= 2
          ? '#f59e0b'
          : '#ef4444'
      : alpha(theme.palette.text.disabled, 0.3);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 20 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Box
          key={i}
          sx={{
            width: 4,
            borderRadius: '1px',
            height: 4 + i * 3,
            bgcolor: i <= bars ? active : alpha(theme.palette.text.disabled, 0.15),
            transition: 'background-color 0.3s',
          }}
        />
      ))}
    </Box>
  );
}

function MetricRow({ label, value, unit, type }) {
  const color = signalColor(value, type);
  const quality = signalLabel(value, type);
  const isNA = value == null || typeof value === 'string';
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.5,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, minWidth: 40 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
            color: isNA ? 'text.disabled' : color || 'text.primary',
          }}
        >
          {isNA ? '\u2014' : `${value} ${unit}`}
        </Typography>
        <Chip
          label={quality}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.6rem',
            fontWeight: 600,
            bgcolor: color ? alpha(color, 0.12) : alpha(theme.palette.text.disabled, 0.08),
            color: color || 'text.disabled',
            minWidth: 40,
          }}
        />
      </Box>
    </Box>
  );
}

function InfoCard({ icon: Icon, title, children, error, accentColor, sx: sxProp }) {
  const theme = useTheme();
  const accent = error ? '#ef4444' : accentColor || theme.palette.primary.main;
  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.mode === 'dark' ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `3px solid ${accent}`,
        boxShadow: 'none',
        ...sxProp,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon sx={{ fontSize: 18, color: accent }} />
        <Typography
          variant="caption"
          sx={{
            color: error ? '#ef4444' : 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.65rem',
          }}
        >
          {title}
        </Typography>
        {error && (
          <Chip
            label="Error"
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              bgcolor: alpha('#ef4444', 0.12),
              color: '#ef4444',
              ml: 'auto',
              fontWeight: 600,
            }}
          />
        )}
      </Box>
      {children}
    </Card>
  );
}

const BASE_POLLING_INTERVAL = 5000;

const StargateModemStatus = ({ ip, apiKey, showLoadingOverlay }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [modemStatus, setModemStatus] = useState({});
  const [signal, setSignal] = useState({});
  const [operator, setOperator] = useState({});
  const [atInput, setAtInput] = useState('AT');
  const [atResult, setAtResult] = useState('');
  const [sending, setSending] = useState(false);
  const [atOpen, setAtOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const intervalRef = useRef();
  const activeRequestsRef = useRef([]);
  const backoff = getGlobalBackoff();
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    const run = async () => {
      await fetchAll(true);
      const schedule = () => {
        if (intervalRef.current) clearTimeout(intervalRef.current);
        const delay = backoff.getDelay(`modem-status-${ip}`) || BASE_POLLING_INTERVAL;
        intervalRef.current = setTimeout(() => {
          fetchAll(false).finally(schedule);
        }, delay);
      };
      schedule();
    };
    run();
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      activeRequestsRef.current.forEach((r) => r && r.cancel && r.cancel());
      activeRequestsRef.current = [];
      backoff.resetKey(`modem-status-${ip}`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ip, apiKey]);

  const fetchAll = useCallback(
    async (isInitial = false) => {
      if (isInitial || !hasLoadedOnceRef.current) setLoading(true);
      activeRequestsRef.current.forEach((r) => r && r.cancel && r.cancel());
      activeRequestsRef.current = [];

      try {
        let cachedData, info, sig, reg;
        let cacheError = false,
          infoError = false,
          sigError = false,
          regError = false;

        const cacheReq = createCancelableRemoteGet(ip, apiKey, '/api/modem/cache');
        const infoReq = createCancelableRemoteGet(ip, apiKey, '/api/modem/info');
        const sigReq = createCancelableRemoteGet(ip, apiKey, '/api/modem/signal');
        const regReq = createCancelableRemoteGet(ip, apiKey, '/api/modem/network');
        activeRequestsRef.current = [cacheReq, infoReq, sigReq, regReq];

        try {
          cachedData = await cacheReq.request;
          if (cachedData) {
            info = cachedData.modem_info || {};
            sig = cachedData.signal_strength || {};
            reg = cachedData.network_info || {};
          }
        } catch (err) {
          if (axios.isCancel(err)) return;
          cacheError = true;
          cachedData = null;
        }

        if (cacheError || !cachedData) {
          try {
            info = await infoReq.request;
          } catch (err) {
            if (axios.isCancel(err)) return;
            infoError = true;
            info = {};
          }
          try {
            sig = await sigReq.request;
          } catch (err) {
            if (axios.isCancel(err)) return;
            sigError = true;
            sig = {};
          }
          try {
            reg = await regReq.request;
          } catch (err) {
            if (axios.isCancel(err)) return;
            regError = true;
            reg = {};
          }
        }
        activeRequestsRef.current = [];

        const infoData = info?.data ?? info ?? {};
        const sigData = sig?.data ?? sig ?? {};
        const regData = reg?.data ?? reg ?? {};
        let parsedModel, parsedImei, signalMetrics, networkInfo;

        if (cachedData && !cacheError) {
          const ci = cachedData.info || cachedData.modem_info || {};
          const cs = cachedData.signal || cachedData.signal_strength || {};
          const cn = cachedData.network || cachedData.network_info || {};
          parsedModel = ci.model || 'Unknown';
          parsedImei = ci.imei || 'Unknown';
          signalMetrics = {
            rssi: cs.rssi ?? null,
            rsrp: cs.rsrp ?? null,
            rsrq: cs.rsrq ?? null,
            sinr: cs.sinr ?? null,
            signal_bars: cs.signal_bars ?? 0,
            signal_quality: cs.signal_quality ?? cs.status ?? 'No Signal',
          };
          networkInfo = {
            operator: ci.operator || 'No Service',
            network_type: cn.type?.replace(/[\r\n]+.*$/, '').trim() || 'Unknown',
            registration: ci.status || 'Not Registered',
            band: cn.band || 'Unknown',
            cell_id: cn.cell_id || 'Unknown',
            lac: cn.lac || 'Unknown',
          };
        } else {
          parsedModel = infoData.model || 'Unknown';
          parsedImei = infoData.imei || 'Unknown';
          signalMetrics = {
            rssi: sigData.rssi ?? null,
            rsrp: sigData.rsrp ?? null,
            rsrq: sigData.rsrq ?? null,
            sinr: sigData.sinr ?? null,
            signal_bars: sigData.signal_bars ?? 0,
            signal_quality: sigData.signal_quality ?? sigData.status ?? 'No Signal',
          };
          networkInfo = {
            operator: infoData.operator || regData.operator || 'No Service',
            network_type: regData.network_type || regData.type || 'Unknown',
            registration: infoData.status || regData.registration || 'Not Registered',
            band: regData.band || 'Unknown',
            cell_id: regData.cell_id || 'Unknown',
            lac: regData.lac || 'Unknown',
          };
        }

        setModemStatus({
          ...infoData,
          parsedModel,
          parsedImei,
          infoError,
          sigError,
          regError,
          cacheError,
        });
        hasLoadedOnceRef.current = true;
        setHasLoadedOnce(true);
        setLoading(false);
        setSignal(signalMetrics);

        if (networkInfo?.operator) {
          setOperator({
            operator: networkInfo.operator,
            network_type: networkInfo.network_type,
            band: networkInfo.band,
            cell_id: networkInfo.cell_id,
            error: false,
          });
        } else {
          try {
            const copsReq = createCancelableRemotePost(ip, apiKey, '/api/modem/command', {
              command: 'AT+COPS?',
            });
            activeRequestsRef.current.push(copsReq);
            const copsRes = await copsReq.request;
            let raw =
              typeof copsRes?.data === 'string'
                ? copsRes.data
                : typeof copsRes?.data?.response === 'string'
                  ? copsRes.data.response
                  : typeof copsRes?.response === 'string'
                    ? copsRes.response
                    : '';
            raw = raw.replace(/[\r\n]+/g, ' ').trim();
            setOperator({
              operator: parseOperator(raw),
              network_type: 'Unknown',
              band: 'Unknown',
              error: false,
            });
          } catch (err) {
            if (axios.isCancel(err)) return;
            setOperator({
              operator: '\u2014',
              network_type: 'Unknown',
              band: 'Unknown',
              error: true,
            });
          }
        }

        if (!infoError && !sigError && !regError) backoff.recordSuccess(`modem-status-${ip}`);
        else backoff.recordFailure(`modem-status-${ip}`);
      } catch (e) {
        if (axios.isCancel(e)) return;
        backoff.recordFailure(`modem-status-${ip}`, e);
        if (!backoff.shouldThrottleError())
          setSnackbar({
            open: true,
            message: 'Failed to fetch modem info',
            severity: 'error',
          });
      }
    },
    [ip, apiKey, backoff]
  );

  const handleSendAT = async () => {
    setSending(true);
    setAtResult('');
    try {
      const res = await remotePost(ip, apiKey, '/api/modem/command', {
        command: atInput,
      });
      let clean;
      if (typeof res?.data === 'string') clean = res.data.replace(/[\r\n]+/g, '\n').trim();
      else if (typeof res?.data === 'object' && res?.data !== null)
        clean = JSON.stringify(res.data, null, 2);
      else clean = res?.data ? String(res.data) : '';
      setAtResult(clean || JSON.stringify(res, null, 2));
      setSnackbar({
        open: true,
        message: 'Command sent successfully',
        severity: 'success',
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: `Command failed: ${e.message || 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  const { isDarkMode } = useAppTheme();
  const hasLTE = signal.rssi != null && typeof signal.rssi !== 'string';
  const overallColor = hasLTE
    ? signalColor(signal.rssi, 'RSSI') || '#4ade80'
    : alpha(theme.palette.text.disabled, 0.4);

  const signalPercent = hasLTE ? Math.min(100, Math.max(0, ((signal.rssi + 113) / 63) * 100)) : 0;
  const signalQualityLabel = hasLTE
    ? signalPercent >= 60
      ? 'Strong'
      : signalPercent >= 30
        ? 'Fair'
        : 'Weak'
    : 'No Signal';

  const registrationStatus = modemStatus.status || 'Unknown';
  const regColor = registrationStatus.toLowerCase().includes('registered')
    ? '#4ade80'
    : registrationStatus.toLowerCase().includes('searching')
      ? '#f59e0b'
      : registrationStatus.toLowerCase().includes('denied')
        ? '#ef4444'
        : alpha(theme.palette.text.disabled, 0.5);

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700, fontSize: 20 }}>
            Modem Management
          </Typography>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasLTE && <SignalBars rssi={signal.rssi} />}
            {hasLoadedOnce && (
              <Chip
                label={hasLTE ? signal.signal_quality || 'Connected' : 'No LTE'}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  bgcolor: alpha(overallColor, 0.15),
                  color: overallColor,
                }}
              />
            )}
            <IconButton size="small" onClick={() => fetchAll(false)} disabled={loading}>
              <RefreshIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        }
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#f5f7fa',
          '& .MuiCardHeader-action': { margin: 0, alignSelf: 'center' },
        }}
      />
      <CardContent sx={{ position: 'relative' }}>
        {loading && !hasLoadedOnce && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {(hasLoadedOnce || !loading) && (
          <>
            {/* Overall Signal Strength Bar */}
            <Card
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CellTowerIcon sx={{ fontSize: 20, color: overallColor }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Overall Signal Strength
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: overallColor }}>
                    {hasLTE ? `${Math.round(signalPercent)}%` : '\u2014'}
                  </Typography>
                  <Chip
                    label={signalQualityLabel}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: overallColor,
                      color: '#fff',
                      height: 22,
                    }}
                  />
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={signalPercent}
                sx={{
                  height: 8,
                  borderRadius: 2,
                  bgcolor: isDarkMode ? 'rgba(138, 148, 168, 0.1)' : 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 2,
                    bgcolor: overallColor,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
              >
                {modemStatus.parsedModel || 'LTE'} &bull;{' '}
                {operator.operator && operator.operator !== '\u2014'
                  ? operator.operator
                  : 'No Carrier'}
                {operator.network_type && operator.network_type !== 'Unknown'
                  ? ` \u2022 ${operator.network_type}`
                  : ''}
              </Typography>
            </Card>

            <Grid container spacing={2}>
              {/* Device */}
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={RouterIcon}
                  title="Device"
                  error={modemStatus.infoError}
                  accentColor="#6366f1"
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: 'text.primary',
                      fontSize: '1rem',
                    }}
                  >
                    {modemStatus.parsedModel || '\u2014'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      mt: 0.5,
                      display: 'block',
                      fontFamily: '"JetBrains Mono", monospace',
                      wordBreak: 'break-all',
                    }}
                  >
                    IMEI: {modemStatus.parsedImei || '\u2014'}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={
                        modemStatus.parsedModel && modemStatus.parsedModel !== 'Unknown'
                          ? 'Detected'
                          : 'Not Found'
                      }
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor:
                          modemStatus.parsedModel && modemStatus.parsedModel !== 'Unknown'
                            ? alpha('#4ade80', 0.12)
                            : alpha('#ef4444', 0.12),
                        color:
                          modemStatus.parsedModel && modemStatus.parsedModel !== 'Unknown'
                            ? '#4ade80'
                            : '#ef4444',
                      }}
                    />
                  </Box>
                </InfoCard>
              </Grid>

              {/* Signal Metrics */}
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={SignalCellularAltIcon}
                  title="LTE Signal"
                  error={modemStatus.sigError}
                  accentColor={overallColor}
                >
                  <MetricRow label="RSSI" value={signal.rssi} unit="dBm" type="RSSI" />
                  <MetricRow label="RSRP" value={signal.rsrp} unit="dBm" type="RSRP" />
                  <MetricRow label="RSRQ" value={signal.rsrq} unit="dB" type="RSRQ" />
                  <MetricRow label="SINR" value={signal.sinr} unit="dB" type="SINR" />
                  {hasLTE && (
                    <LinearProgress
                      variant="determinate"
                      value={signalPercent}
                      sx={{
                        mt: 1,
                        height: 4,
                        borderRadius: 2,
                        bgcolor: alpha(overallColor, 0.1),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 2,
                          bgcolor: overallColor,
                        },
                      }}
                    />
                  )}
                </InfoCard>
              </Grid>

              {/* Operator */}
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={PublicIcon}
                  title="Operator"
                  error={operator.error}
                  accentColor="#0098DF"
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: 'text.primary',
                      fontSize: '1rem',
                    }}
                  >
                    {operator.operator || '\u2014'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
                  >
                    {operator.network_type || '\u2014'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    Band: {operator.band || '\u2014'}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={
                        operator.operator &&
                        operator.operator !== '\u2014' &&
                        operator.operator !== 'No Service'
                          ? 'Connected'
                          : 'No Service'
                      }
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor:
                          operator.operator &&
                          operator.operator !== '\u2014' &&
                          operator.operator !== 'No Service'
                            ? alpha('#4ade80', 0.12)
                            : alpha('#f59e0b', 0.12),
                        color:
                          operator.operator &&
                          operator.operator !== '\u2014' &&
                          operator.operator !== 'No Service'
                            ? '#4ade80'
                            : '#f59e0b',
                      }}
                    />
                  </Box>
                </InfoCard>
              </Grid>

              {/* Network / Registration */}
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard icon={SimCardIcon} title="Network" accentColor={regColor}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {operator.cell_id && operator.cell_id !== 'Unknown'
                      ? `Cell: ${operator.cell_id}`
                      : 'Cell: \u2014'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
                  >
                    Status: {registrationStatus}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {modemStatus.parsedImei && modemStatus.parsedImei !== 'Unknown'
                      ? 'IMEI verified'
                      : 'IMEI not available'}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={registrationStatus}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: alpha(regColor, 0.12),
                        color: regColor,
                      }}
                    />
                  </Box>
                </InfoCard>
              </Grid>
            </Grid>

            {/* AT Command Console (collapsible) */}
            <Card
              elevation={0}
              sx={{
                mt: 2,
                overflow: 'hidden',
                background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
              }}
            >
              <Box
                onClick={() => setAtOpen((o) => !o)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.action.hover, 0.04),
                  },
                }}
              >
                <TerminalIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    flex: 1,
                  }}
                >
                  AT Command Console
                </Typography>
                {atOpen ? (
                  <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                )}
              </Box>
              <Collapse in={atOpen}>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="AT+COPS?"
                      value={atInput}
                      onChange={(e) => setAtInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !sending) handleSendAT();
                      }}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                          fontSize: '0.85rem',
                        },
                      }}
                    />
                    <Tooltip title="Send command">
                      <span>
                        <IconButton
                          onClick={handleSendAT}
                          disabled={sending}
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': { bgcolor: 'primary.dark' },
                            '&.Mui-disabled': {
                              bgcolor: alpha(theme.palette.primary.main, 0.3),
                              color: alpha(theme.palette.primary.contrastText, 0.5),
                            },
                          }}
                        >
                          {sending ? (
                            <CircularProgress size={18} sx={{ color: 'primary.contrastText' }} />
                          ) : (
                            <SendIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  {atResult && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.common.black, 0.3),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      <Typography
                        component="pre"
                        sx={{
                          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                          fontSize: '0.8rem',
                          color: 'success.light',
                          whiteSpace: 'pre-wrap',
                          m: 0,
                        }}
                      >
                        {typeof atResult === 'string'
                          ? atResult
                          : JSON.stringify(atResult, null, 2)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Card>
          </>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            sx={{ fontWeight: 600 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default StargateModemStatus;
